"use client";

/**
 * Блок брони (телефон + код страны + согласия + «Продолжить» → createBooking →
 * /booking?b=uid). Копия BookingPanel со страницы машины — вынесена сюда, чтобы
 * её мог переиспользовать инлайн-результат на странице темы (/themes/[slug]),
 * НЕ трогая CarDetailClient. Нужны минимум: car {id,title,price}, city {station,slug}.
 */

import { useState, useEffect, useRef } from "react";
import { formatPhone, isPhoneComplete } from "@/lib/phoneMask";
import { useRouter } from "next/navigation";
import { type Country } from "@/lib/countries";
import { createBooking } from "@/lib/api";
import { useToast } from "@/components/ui/Toast";
import { track } from "@/lib/metrika";
import { SORTED_COUNTRIES, getPlaceholder } from "@/components/VerticalSearchForm";
import { readPhoneCookie, writePhoneCookie } from "@/lib/client-cookie";
import { useLocale } from "@/lib/locale-context";

export type BookingPanelCar = { id: string; title: string; price: number };
export type BookingPanelCity = { station: string; slug: string };

export default function BookingPanel({
  car,
  city,
  rentalDays,
  dateFrom,
  dateTo,
  startTime,
  endTime,
  regionGuid,
  onClose,
  currency,
}: {
  car: BookingPanelCar;
  city: BookingPanelCity;
  rentalDays: number;
  dateFrom: string;
  dateTo: string;
  startTime: string;
  endTime: string;
  regionGuid: string;
  onClose: () => void;
  currency: string;
}) {
  const router = useRouter();
  const { t, locale } = useLocale();
  const toast = useToast();
  const cur = currency;
  const dayWord = locale === "en"
    ? (rentalDays === 1 ? "day" : "days")
    : (rentalDays === 1 ? "день" : rentalDays < 5 ? "дня" : "дней");
  const [phone, setPhone] = useState("");
  const [country, setCountry] = useState<Country>(SORTED_COUNTRIES[0]);
  const [agreePrivacy, setAgreePrivacy] = useState(false);
  const [agreePersonal, setAgreePersonal] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Запоминание телефона между бронями/формами (как «last_phone» на Солнышке).
  // Cookie формат "<dial> <masked>" — вытаскиваем dial, ищем страну (для маски),
  // подставляем локальную часть.
  useEffect(() => {
    const cookie = readPhoneCookie();
    if (!cookie) return;
    const sp = cookie.indexOf(" ");
    const dial = sp > 0 ? cookie.slice(0, sp) : "";
    const rest = sp > 0 ? cookie.slice(sp + 1) : cookie;
    if (dial) {
      const match = SORTED_COUNTRIES.find((c) => c.dial === dial);
      if (match) setCountry(match);
    }
    if (rest) setPhone(rest);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    if (phone) writePhoneCookie(`${country.dial} ${phone}`.trim());
  }, [phone, country.dial]);
  const searchRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
        setSearch("");
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (dropdownOpen && searchRef.current) searchRef.current.focus();
  }, [dropdownOpen]);

  const filtered = search.trim()
    ? SORTED_COUNTRIES.filter(
        (c) =>
          c.name.toLowerCase().includes(search.toLowerCase()) ||
          c.dial.includes(search) ||
          c.code.toLowerCase().includes(search.toLowerCase())
      )
    : SORTED_COUNTRIES;

  const pricePerDay = car.price;
  const totalPrice = pricePerDay * rentalDays;

  return (
    <div className="booking-panel-wrap" style={{ display: "flex", flexDirection: "column", width: "100%" }}>
      {/* keyframe для спиннера — на случай, если форма поиска не на странице */}
      <style>{`@keyframes sfp-spin { to { transform: rotate(360deg); } }`}</style>
      <div className="booking-panel-inner" style={{ display: "flex", width: "100%", position: "relative" }}>
        {/* Details */}
        <div
          className="booking-panel-details"
          style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "flex-start", justifyContent: "center", padding: "0", minWidth: 0 }}
        >
          {/* Close button */}
          <button
            className="booking-panel-close"
            onClick={onClose}
            style={{ alignSelf: "flex-end", background: "none", border: "none", cursor: "pointer", padding: 4, marginTop: -8, marginRight: -8, marginBottom: 4 }}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#999" strokeWidth="2" strokeLinecap="round">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>

          {/* Title */}
          <span style={{ color: "#000", fontSize: 20, fontWeight: "bold", marginBottom: 14 }}>{t("booking.title")}</span>

          {/* Phone input block */}
          <div
            style={{ display: "flex", flexDirection: "column", background: "#EDEDED", borderRadius: 10, padding: "16px 20px", marginBottom: 16, width: "100%", boxSizing: "border-box", gap: 8 }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <span style={{ color: "#000", fontSize: 13, fontWeight: "bold", width: 96, flexShrink: 0 }}>{t("booking.countryCode")}</span>
              <span style={{ color: "#000", fontSize: 13, fontWeight: "bold", flex: 1, minWidth: 0 }}>{t("booking.contactData")}</span>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              {/* Country code */}
              <div ref={dropdownRef} style={{ position: "relative", flexShrink: 0 }}>
                <button
                  onClick={() => { setDropdownOpen(!dropdownOpen); setSearch(""); }}
                  style={{ display: "flex", alignItems: "center", background: "#FFFFFF", borderRadius: 10, border: "none", padding: "10px 12px", cursor: "pointer", gap: 5 }}
                >
                  <span style={{ fontSize: 16 }}>{country.flag}</span>
                  <span style={{ color: "#000", fontSize: 14, fontWeight: 500 }}>{country.dial}</span>
                  <svg width="14" height="14" viewBox="0 0 12 12" fill="none" style={{ marginLeft: 4, transition: "transform 0.2s", transform: dropdownOpen ? "rotate(180deg)" : "rotate(0deg)" }}>
                    <path d="M3 4.5L6 7.5L9 4.5" stroke="#888" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </button>

                {dropdownOpen && (
                  <div
                    style={{ position: "absolute", bottom: "calc(100% + 4px)", left: 0, background: "#FFFFFF", borderRadius: 10, boxShadow: "0 8px 30px rgba(0,0,0,0.2)", zIndex: 9999, width: 260, maxHeight: 280, display: "flex", flexDirection: "column", overflow: "hidden" }}
                  >
                    <div style={{ padding: "10px 12px 6px" }}>
                      <input
                        ref={searchRef}
                        placeholder={t("booking.searchCountry")}
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        style={{ width: "100%", padding: "8px 12px", border: "1px solid #E0E0E0", borderRadius: 8, fontSize: 14, outline: "none", boxSizing: "border-box" }}
                        onFocus={(e) => { e.target.style.borderColor = "#0039A6"; }}
                        onBlur={(e) => { e.target.style.borderColor = "#E0E0E0"; }}
                      />
                    </div>
                    <div style={{ overflowY: "auto", flex: 1 }}>
                      {filtered.length === 0 && (
                        <div style={{ padding: "12px 16px", color: "#999", fontSize: 14 }}>{t("common.notFound")}</div>
                      )}
                      {filtered.map((c) => (
                        <button
                          key={c.code}
                          onClick={() => { setCountry(c); setDropdownOpen(false); setSearch(""); setPhone(""); }}
                          style={{ display: "flex", alignItems: "center", gap: 10, width: "100%", padding: "8px 16px", border: "none", background: country.code === c.code ? "#dfe8f6" : "transparent", cursor: "pointer", textAlign: "left", transition: "background 0.1s" }}
                          onMouseEnter={(e) => { e.currentTarget.style.background = "#dfe8f6"; }}
                          onMouseLeave={(e) => { e.currentTarget.style.background = country.code === c.code ? "#dfe8f6" : "transparent"; }}
                        >
                          <span style={{ fontSize: 16 }}>{c.flag}</span>
                          <span style={{ fontSize: 13, color: "#333", flex: 1 }}>{c.name}</span>
                          <span style={{ fontSize: 13, color: "#888" }}>{c.dial}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Phone input */}
              <input
                className="booking-panel-phone-input"
                type="tel"
                inputMode="numeric"
                autoComplete="tel-national"
                placeholder={getPlaceholder(country.mask, t("phone.number"))}
                value={phone}
                onChange={(e) => {
                  const raw = e.target.value.replace(/\D/g, "");
                  setPhone(formatPhone(raw, country.code));
                }}
                style={{ color: "#000", fontSize: 14, flex: 1, background: "#FFFFFF", borderRadius: 10, border: "none", padding: "10px 12px", outline: "none" }}
              />
            </div>

            {/* Consent checkboxes */}
            <div style={{ display: "flex", flexDirection: "column", gap: 6, marginTop: 4 }}>
              <div style={{ display: "flex", alignItems: "flex-start", gap: 10, fontSize: 12, color: "#2B2F33", lineHeight: 1.4 }}>
                <input type="checkbox" checked={agreePrivacy} onChange={(e) => setAgreePrivacy(e.target.checked)} style={{ marginTop: 2, width: 16, height: 16, flexShrink: 0, cursor: "pointer", accentColor: "#0039A6" }} />
                <span onClick={() => setAgreePrivacy(!agreePrivacy)} style={{ cursor: "pointer" }}>{t("consent.privacyPrefix")} <a href="/legal/privacy" target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} style={{ color: "#0039A6", textDecoration: "underline" }}>{t("consent.privacySite")}</a></span>
              </div>
              <div style={{ display: "flex", alignItems: "flex-start", gap: 10, fontSize: 12, color: "#2B2F33", lineHeight: 1.4 }}>
                <input type="checkbox" checked={agreePersonal} onChange={(e) => setAgreePersonal(e.target.checked)} style={{ marginTop: 2, width: 16, height: 16, flexShrink: 0, cursor: "pointer", accentColor: "#0039A6" }} />
                <span onClick={() => setAgreePersonal(!agreePersonal)} style={{ cursor: "pointer" }}>{t("consent.personalPrefix2")} <a href="/legal/personal-data" target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} style={{ color: "#0039A6", textDecoration: "underline" }}>{t("consent.personalData")}</a></span>
              </div>
            </div>
          </div>

          {/* Price + Book button */}
          <div style={{ display: "flex", alignItems: "center", gap: 20, width: "100%" }}>
            <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <span style={{ color: "#000", fontSize: 13 }}>{t("booking.total")}</span>
              <span style={{ color: "#d52b1e", fontSize: 20, fontWeight: "bold" }}>
                {totalPrice.toLocaleString("ru-RU")} {cur}
              </span>
              <span style={{ color: "#888", fontSize: 11 }}>
                {pricePerDay.toLocaleString("ru-RU")} {cur}{t("booking.perDay")} × {rentalDays} {dayWord}
              </span>
            </div>
            <button
              disabled={submitting}
              onClick={async () => {
                if (submitting) return;
                if (!phone.trim() || !isPhoneComplete(phone, country.code)) {
                  toast.show(locale === "en" ? "Enter a valid phone number" : "Введите корректный номер телефона", "error");
                  return;
                }
                if (!agreePrivacy || !agreePersonal) { toast.show(t("consent.confirmRequired"), "error"); return; }
                if (!dateFrom || !dateTo) { toast.show(locale === "en" ? "Select rental dates" : "Выберите даты аренды", "error"); return; }
                setSubmitting(true);
                const fullPhone = `${country.dial} ${phone}`.trim();
                try {
                  const uid = await createBooking({
                    phone: fullPhone,
                    carId: car.id,
                    carName: car.title,
                    dateFrom,
                    dateTo,
                    startTime,
                    endTime,
                    regionGuid: regionGuid || "",
                    station: city.station,
                    citySlug: city.slug,
                  });
                  if (uid) {
                    track("booking_start", { city: city.slug || city.station });
                    router.push(`/booking?b=${encodeURIComponent(uid)}`);
                  } else {
                    setSubmitting(false);
                  }
                } catch {
                  setSubmitting(false);
                }
              }}
              style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 10, background: "#0039A6", borderRadius: 10, border: "none", padding: "14px 0", cursor: submitting ? "not-allowed" : "pointer", opacity: submitting ? 0.85 : 1 }}
            >
              {submitting && (
                <span style={{ width: 16, height: 16, border: "2px solid rgba(255,255,255,0.45)", borderTopColor: "#fff", borderRadius: "50%", display: "inline-block", animation: "sfp-spin 0.8s linear infinite" }} />
              )}
              <span style={{ color: "#FFFFFF", fontSize: 16, fontWeight: "bold" }}>
                {submitting ? t("booking.loading") : t("btn.continue")}
              </span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
